import { useState, useEffect } from 'react'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, useWatch } from 'react-hook-form'
import { useSnackbar } from 'notistack'
// @mui
import {
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  RadioGroup,
  Radio,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { ChevronLeft } from '@mui/icons-material'
// components
import Iconify from 'components/Iconify'
import { FormProvider, RHFTextField } from 'components/hook-form'
import { StyledBaseContainer } from '~/commons/styled'
import FileUploadOutlined, {
  FileNameTypography,
} from 'commons/components/FileUploadOutlined'
import { FileUploadstyle } from 'components/upload/UploadOutlined'
// hooks
import { usePreventUnsavedChanges } from 'hooks/usePreventUnsavedChanges'
import { useUserKYC } from 'domains/Users/Verification/hooks/useVerificationUser'
// helpers
import { useBoundVerificationUserStore } from 'domains/Users/Verification/stores'
import { getErrorMessage } from 'helpers/error'
import { makeXMLRequest } from 'helpers/make-xml-request'
// services
import { putNpwpKYC } from '~/services/user'
import { postPresignURL } from '~/services/user'
// types
import { INPWP } from 'domains/Users/Verification/stores/VerificationUser/interface'
import { useRouter } from 'next/router'
import useAuth from 'hooks/useAuth'
import trackEvent from '~/trackers'
import { PATH_DASHBOARD } from 'routes/paths'
import useLocales from 'hooks/useLocales'

export default function NpwpForm() {
  const { t } = useLocales()
  const { query, pathname, isReady, push } = useRouter()
  const { user } = useAuth()
  const trackerData = { query, pathname, user }
  const [NpwpCardPhotoProgress, setNpwpCardPhotoProgress] =
    useState(0)
  const [idCardPhotoRequest, setIdCardPhotoRequest] =
    useState<XMLHttpRequest>(null)
  const [isNpwpCardPhotoUploading, setIsNpwpCardPhotoUploading] =
    useState(false)
  const [NpwpPhotoUploadError, setNpwpCardPhotoUploadError] =
    useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const BUSINESS = user?.creator?.creator_class === 'BUSINESS'
  const CREATOR = user?.creator?.creator_class === 'CREATOR'
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()
  const { setActiveStep, personalData, errorRejection } =
    useBoundVerificationUserStore((state) => ({
      personalData: state.personalData,
      setActiveStep: state.setActiveStep,
      errorRejection: state.errorRejection,
    }))

  const { unsubscribeToRouteChangeGuard } = usePreventUnsavedChanges(
    '/dashboard/verification',
  )

  const DEFAULT_NPWP_STATE = {
    npwp_number: '',
    npwp_type: CREATOR ? 'PRIBADI' : 'PERUSAHAAN',
    npwp_photo: '',
    npwp_photo_file_name: '',
    have_npwp: true,
  }

  const NPWP_SCHEMA = Yup.object().shape({
    npwp_number: Yup.string().when('have_npwp', {
      is: (have_npwp) => Boolean(have_npwp),
      then: (schema) =>
        schema.test({
          name: 'validate-npwp-number',
          test(value, { createError, parent }) {
            if (value === '') {
              return createError({
                message: t('key_npwp_number_must_be_filled_in'),
              })
            }

            if (parent.npwp_type === 'PRIBADI') {
              if (value.length < 15 || value.length > 16) {
                return createError({
                  message: t(
                    'key_npwp_number_must_be_15_16_characters',
                  ),
                })
              }
            } else if (parent.npwp_type === 'PERUSAHAAN') {
              if (value.length !== 16) {
                return createError({
                  message: t('key_npwp_number_must_be_16_characters'),
                })
              }
            }

            return true
          },
        }),
    }),
  })

  const form = useForm<INPWP>({
    resolver: yupResolver(NPWP_SCHEMA),
    defaultValues: DEFAULT_NPWP_STATE,
    mode: 'onChange',
  })
  const {
    trigger,
    setValue,
    control,
    handleSubmit,
    formState,
    clearErrors,
  } = form
  const { isValid } = formState

  const { refetch: getUserKYC } = useUserKYC()
  const [
    have_npwp,
    npwp_number,
    npwp_type,
    npwp_photo,
    npwp_photo_file_name,
  ] = useWatch({
    control,
    name: [
      'have_npwp',
      'npwp_number',
      'npwp_type',
      'npwp_photo',
      'npwp_photo_file_name',
    ],
  })

  useEffect(() => {
    if (personalData) {
      setValue('have_npwp', true)
      if (personalData.have_npwp) {
        setValue('npwp_number', personalData.npwp_number)
        setValue('npwp_type', personalData.npwp_type)
        setValue('npwp_photo', personalData.npwp_photo)
        setValue(
          'npwp_photo_file_name',
          personalData.npwp_photo_file_name,
        )
      }

      if (
        personalData.npwp_number ||
        personalData.npwp_photo ||
        personalData.npwp_photo_file_name
      ) {
        trigger()
      }
    }
  }, [personalData])

  const handleFileDropIdCard = async (file: File) => {
    try {
      trackEvent.user('click_upload_npwp', trackerData)
      setIsNpwpCardPhotoUploading(true)
      const filename = file?.name
      const responseFile = await postPresignURL({
        target: 'npwp_photo',
        filename,
      })
      if (responseFile?.data?.code === 'SUCCESS') {
        const urlBlob = URL.createObjectURL(file)
        const blob = await (await fetch(urlBlob)).blob()
        await makeXMLRequest({
          headers: responseFile?.data?.data?.presign_url_headers,
          contentType: file.type,
          url: responseFile.data.data.presign_url,
          blob,
          handleUploadProgress: setNpwpCardPhotoProgress,
          setRequest: setIdCardPhotoRequest,
          handleError: () => setNpwpCardPhotoUploadError(true),
        })
        setValue(
          'npwp_photo_file_name',
          responseFile.data.data.file_name,
        )
        return responseFile.data.data.file_name
      }
    } catch (error) {
      setNpwpCardPhotoUploadError(true)
    } finally {
      setIsNpwpCardPhotoUploading(false)
    }
  }

  const handleDeletePhotoNpwpCard = () => {
    idCardPhotoRequest?.abort()
    setValue('npwp_photo', null)
    setValue('npwp_photo_file_name', '')
    setNpwpCardPhotoUploadError(false)
  }

  const onActionButtonSubmit = async (data) => {
    try {
      setIsLoading(true)
      const payload = {
        npwp_number: data.npwp_number,
        npwp_type: data.npwp_type,
        npwp_photo: data.npwp_photo_file_name,
      }

      const result = await putNpwpKYC(payload)
      if (result?.data?.code === 'SUCCESS') {
        unsubscribeToRouteChangeGuard()
        setActiveStep(3)
        getUserKYC()
        push(
          PATH_DASHBOARD.general.verification.root +
            '?source=UPLOAD_DOCUMENT&source_action=click_next_to_review_document',
        )
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  const hideNIK =
    personalData.id_card_type === 2 || npwp_type === 'PERUSAHAAN'

  useEffect(() => {
    if (isReady) trackEvent.user('visit_npwp', trackerData)
  }, [isReady])

  return (
    <FormProvider
      methods={form}
      onSubmit={handleSubmit(onActionButtonSubmit)}
    >
      <StyledBaseContainer
        sx={
          errorRejection?.npwpPhoto?.length > 0 &&
          have_npwp && {
            paddingBottom: '100px',
            [theme.breakpoints.up('sm')]: {
              paddingBottom: '60px',
            },
            [theme.breakpoints.up('md')]: {
              paddingBottom: '70px',
            },
          }
        }
      >
        <Grid container spacing={3}>
          <Grid item gap={0.5} display="flex" flexDirection="column">
            <Typography variant="h6">{t('key_NPWP')}</Typography>
            <Typography
              variant="body2"
              sx={(theme) => ({ color: theme.palette.grey[600] })}
            >
              {t('key_enter_data_and_upload_npwp')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControl
              sx={{
                display: 'flex',
              }}
            >
              <RadioGroup
                row
                value={have_npwp}
                onChange={(event) => {
                  trackEvent.user('click_npwp_satus', trackerData, {
                    is_npwp_available: event.target.value === 'true',
                  })
                  setValue('have_npwp', event.target.value === 'true')
                }}
              >
                <Grid container sx={{ padding: '0px 16px' }}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      value={true}
                      control={<Radio id="i-users-npwpform-have-npwp-true" size="small" />}
                      label={
                        <Typography variant="button">
                          {t('key_have_NPWP')}
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>
              </RadioGroup>
            </FormControl>
          </Grid>
          {have_npwp && (
            <>
              <Grid item xs={12}>
                <FormControl
                  sx={{
                    display: 'flex',
                  }}
                >
                  <RadioGroup
                    row
                    value={npwp_type}
                    onChange={(event) => {
                      trackEvent.user(
                        'click_npwp_type',
                        trackerData,
                        {
                          npwp_type:
                            event.target.value?.toLowerCase(),
                        },
                      )
                      setValue('npwp_type', event.target.value)
                      setValue('npwp_number', '', {
                        shouldValidate: true,
                      })
                    }}
                  >
                    <Grid container sx={{ padding: '0px 16px' }}>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          value="PRIBADI"
                          disabled={BUSINESS}
                          control={<Radio id="i-users-npwpform-npwp-type-pribadi" size="small" />}
                          label={
                            <Typography
                              variant="button"
                              color={
                                BUSINESS && theme.palette.grey[500]
                              }
                            >
                              {t('key_personal')}
                            </Typography>
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          disabled={CREATOR}
                          value="PERUSAHAAN"
                          control={<Radio id="i-users-npwpform-npwp-type-perusahaan" size="small" />}
                          label={
                            <Typography
                              variant="button"
                              color={
                                CREATOR && theme.palette.grey[500]
                              }
                            >
                              {t('key_company')}
                            </Typography>
                          }
                        />
                      </Grid>
                    </Grid>
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid item xs={12} direction="row">
                <Stack
                  gap={3}
                  sx={{
                    flexDirection: 'column',
                    [theme.breakpoints.up('sm')]: {
                      flexDirection: 'row',
                    },
                  }}
                >
                  {!hideNIK && (
                    <Stack gap={2} flex={hideNIK ? 1 : 0.5}>
                      <Stack gap={0.5}>
                        <Typography variant="h6">NIK</Typography>
                        <Typography
                          variant="body2"
                          sx={(theme) => ({
                            color: theme.palette.grey[600],
                          })}
                        >
                          {t('key_enter_NIK')}
                        </Typography>
                      </Stack>
                      <RHFTextField
                        id="i-users-npwpform-nik"
                        disabled
                        required
                        name=""
                        label="NIK"
                        value={personalData?.id_card || ''}
                        placeholder="NIK"
                        sx={(theme) => ({
                          '.MuiInputBase-root.Mui-disabled': {
                            backgroundColor:
                              theme.palette.grey[500_12],
                          },
                        })}
                      />
                    </Stack>
                  )}

                  <Stack gap={2} flex={hideNIK ? 1 : 0.5}>
                    <Stack gap={0.5}>
                      <Typography variant="h6">
                        {t('key_NPWP_number')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={(theme) => ({
                          color: theme.palette.grey[600],
                        })}
                      >
                        {t('key_enter_NPWP_number')}
                      </Typography>
                    </Stack>
                    <RHFTextField
                      id="i-users-npwpform-npwp-number"
                      required
                      name="npwp_number"
                      label={t('key_NPWP_number')}
                      value={npwp_number}
                      placeholder={t('key_NPWP_number')}
                      onChange={(e) => {
                        let value = e.target.value
                        if (value === '') {
                          setValue('npwp_number', '', {
                            shouldValidate: true,
                          })
                        } else if (value.match(/^\d+$/)) {
                          setValue('npwp_number', value, {
                            shouldValidate: true,
                          })
                        }
                      }}
                      onFocus={() =>
                        trackEvent.user(
                          'click_npwp_fill_in',
                          trackerData,
                        )
                      }
                    />
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} direction="row">
                <Grid
                  item
                  gap={0.5}
                  display="flex"
                  flexDirection="column"
                >
                  <Typography variant="h6">
                    {t('key_upload_NPWP')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({
                      color: theme.palette.grey[600],
                    })}
                  >
                    {t('key_upload_a_photo_of_your_NPWP')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({
                      color: theme.palette.grey[600],
                    })}
                  >
                    <ul
                      style={{
                        margin: '0px 0px 0px 24px',
                        padding: 0,
                      }}
                    >
                      <li>{t('key_upload_npwp_in_the_format')}</li>
                      <li>{t('key_make_sure_the_npwp_is_expose')}</li>
                      <li>
                        {t(
                          'key_make_sure_the_npwp_photo_matches_your',
                        )}
                      </li>
                    </ul>
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6}>
                {typeof npwp_photo === 'string' &&
                npwp_photo !== '' ? (
                  <FileUploadstyle
                    direction={'column'}
                    spacing={2}
                    sx={{
                      width: '100%',
                      padding: 0,
                      backgroundImage: `url("${npwp_photo}")`,
                      backgroundPosition: 'center',
                      backgroundSize: 'cover',
                    }}
                  />
                ) : (
                  <FileUploadOutlined
                    name="npwpPhoto"
                    label=""
                    caption={t('key_upload_NPWP')}
                    sx={{
                      height: '163px',
                      background: '#F8F5FF',
                    }}
                    type="image"
                    accept={{
                      'image/jpg': [],
                      'image/jpeg': [],
                      'image/png': [],
                    }}
                    showProgress
                    processing={isNpwpCardPhotoUploading}
                    isError={NpwpPhotoUploadError}
                    progress={NpwpCardPhotoProgress}
                    value={npwp_photo as any}
                    onDrop={handleFileDropIdCard}
                    onDelete={handleDeletePhotoNpwpCard}
                    onFileDialogOpen={() => clearErrors('npwp_photo')}
                  />
                )}
                {npwp_photo ? (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ marginBottom: '16px' }}
                  >
                    <FileNameTypography>
                      {t('key_npwp_photo')}
                    </FileNameTypography>
                    <Iconify
                      id="b-user-delete-npwp-photo"
                      icon="bxs:trash-alt"
                      color={theme.palette.primary.main}
                      onClick={handleDeletePhotoNpwpCard}
                      width={14}
                      height={14}
                      sx={{
                        minWidth: '14px',
                        minHeight: '14px',
                        cursor: 'pointer',
                      }}
                    />
                  </Stack>
                ) : (
                  <Typography
                    variant="caption"
                    sx={(theme) => ({
                      color: theme.palette.grey[600],
                    })}
                  >
                    {errorRejection?.npwpPhoto?.length > 0
                      ? ''
                      : t('key_max_file-size_5mb')}
                  </Typography>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </StyledBaseContainer>
      <Stack
        sx={{ marginTop: '12px' }}
        justifyContent={'space-between'}
        alignItems={'center'}
        direction={'row'}
      >
        <Button
          id="b-user-back-to-dashboard"
          startIcon={<ChevronLeft />}
          sx={(theme) => ({
            color: theme.palette.grey[800],
            ':hover': {
              background: 'transparent',
              opacity: 0.7,
            },
          })}
          onClick={() => {
            unsubscribeToRouteChangeGuard()
            setActiveStep(1)
          }}
        >
          {t('key_back')}
        </Button>
        <Button
          id="b-users-npwpform-submit"
          variant="contained"
          type="submit"
          sx={{ marginLeft: '8px' }}
          endIcon={<Iconify icon={'eva:arrow-ios-forward-fill'} />}
          size="large"
          disabled={
            have_npwp || BUSINESS
              ? !isValid ||
                Boolean(!npwp_photo_file_name) ||
                isLoading
              : false
          }
        >
          {t('key_save_and_continue')}
        </Button>
      </Stack>
    </FormProvider>
  )
}
